/**
 * QueueInfoPanel - Queue Information Tab Content
 *
 * Displays queue statistics from Task Router with expandable sections
 * showing patients in each queue. Features:
 * - Multi-select queue filter
 * - Expandable/collapsible queue sections
 * - Patient count badges
 * - Longest wait time indicator
 */

import { useMemo, useState } from 'react';
import { Card, Tag, Typography, Space, Empty, Badge, Switch } from 'antd';
import { UserOutlined, TeamOutlined } from '@ant-design/icons';
import { useUnifiedDashboardStore } from '../../store';
import { formatWaitMinutes } from '../../utils/timeUtils';
import {
  parseCallPatients,
  parseTriageCatFromCall,
} from '../../utils/callUtils';

const { Text } = Typography;

const DEFAULT_MONITORED_QUEUES = [
  'DR_BOARDROUNDS',
  'DR_CCAS',
  'DR_CRITICAL',
  'DR_GP',
  'DR_PRIORITY',
  'DR_RESERVE_A',
  'DR_RESERVE_B',
  'DR_RESERVE_C',
  'DR_SDPR_CRITICAL',
  'DR_SDPR_PRIORITY',
  'DR_SDPR_STANDARD',
  'DR_SDPR_URGENT_CLINICALLY',
  'DR_STANDARD',
  'DR_URGENT_CLINICALLY',
  'DR_VAS',
  'PSO',
  'PSO_ADMIN',
  'PSO_AMBULANCE',
  'PSO_CCAS',
  'PSO_HNE',
  'PSO_PHN',
  'PSO_SHIFT_CO_ORD',
];

const HIDDEN_CALL_STATUSES = ['assigned', 'wrapping'];

const QueueInfoPanel = () => {
  const { queueInfo } = useUnifiedDashboardStore();
  const [showAllQueues, setShowAllQueues] = useState(false);
  const [showWaitingOnly, setShowWaitingOnly] = useState(true);

  const queues = useMemo(() => queueInfo || [], [queueInfo]);

  const monitoredQueues = useMemo(
    () =>
      queues.filter((queue) => DEFAULT_MONITORED_QUEUES.includes(queue.name)),
    [queues]
  );

  // Candidate pool driven only by the "Monitored queues only" toggle.
  const candidateQueues = useMemo(() => {
    if (showAllQueues || monitoredQueues.length === 0) {
      return queues;
    }
    return monitoredQueues;
  }, [monitoredQueues, queues, showAllQueues]);

  const queueGridTemplateColumns = 'repeat(auto-fill, minmax(360px, 1fr))';

  // Get status color for task status
  const getStatusColor = (status) => {
    switch (status) {
      case 'reserved':
        return 'blue';
      case 'assigned':
        return 'cyan';
      case 'wrapping':
        return 'orange';
      case 'pending':
      default:
        return 'default';
    }
  };

  // Translate status to display label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'reserved':
        return 'in queue';
      case 'assigned':
        return 'in call';
      case 'wrapping':
        return 'wrapping';
      default:
        return status;
    }
  };

  // parseCallPatients imported from '../../utils/callUtils'

  // parseTriageCatFromCall imported from '../../utils/callUtils'

  // formatWaitMinutes imported from '../../utils/timeUtils'

  // Get triage category color
  const getTriageCatColor = (cat) => {
    if (!cat) return null;
    const catUpper = cat.toUpperCase();
    if (catUpper.includes('1')) return 'red';
    if (catUpper.includes('2')) return 'orange';
    if (catUpper.includes('3')) return 'gold';
    if (catUpper.includes('4')) return 'blue';
    if (catUpper.includes('5')) return 'green';
    return 'default';
  };

  const getTriageBorderColor = (cat) => {
    switch (getTriageCatColor(cat)) {
      case 'red':
        return '#ff4d4f';
      case 'orange':
        return '#fa8c16';
      case 'green':
        return '#52c41a';
      case 'gold':
        return '#d4b106';
      case 'blue':
      default:
        return '#1890ff';
    }
  };

  // parseAsLocalTime imported from '../../utils/timeUtils'

  const getQueueCallEntries = (queue) => {
    return (queue.patients || []).map((call, idx) => {
      const patients = parseCallPatients(call);
      const triageCat = parseTriageCatFromCall(call);

      // Build all patient names from the patients array
      const patientNames = patients
        .map((p) =>
          `${p.patientFirstName || ''} ${p.patientLastName || ''}`.trim()
        )
        .filter(Boolean);

      // Wait time ONLY from Realtime table - no fallback calculation
      const waitTime = call.realtimeWaitTime ?? null;

      return {
        key: call.taskSid || `${queue.sid}-${idx}`,
        patientNames:
          patientNames.length > 0 ? patientNames : ['Unknown Patient'],
        triageCat,
        waitTime,
        elapsedTime: call.realtimeElapsedTime ?? null,
        hasRealtimeData: call.hasRealtimeData,
        call,
      };
    });
  };

  // Apply the "Waiting only" filter per queue, then drop any queue that ends up
  // with no visible patients (all calls in-call/wrapping and hidden by the
  // filter, or the queue simply has nobody). Both summary counts derive from
  // this list so they stay in sync with the two toggles (Monitored queues only
  // + Waiting only).
  const displayedQueues = candidateQueues
    .map((queue) => {
      const callEntries = getQueueCallEntries(queue);
      const visibleCallEntries = showWaitingOnly
        ? callEntries.filter(
            ({ call }) => !HIDDEN_CALL_STATUSES.includes(call.currentStatus)
          )
        : callEntries;
      return { queue, visibleCallEntries };
    })
    .filter(({ visibleCallEntries }) => visibleCallEntries.length > 0);

  const totalTasks = displayedQueues.reduce(
    (sum, { visibleCallEntries }) => sum + visibleCallEntries.length,
    0
  );

  const patientListMaxHeight = displayedQueues.length <= 3 ? 340 : 280;

  if (!queues || queues.length === 0) {
    return (
      <div style={{ padding: '24px 0' }}>
        <Empty
          description="No queue data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Summary Stats */}
      <div
        style={{
          display: 'flex',
          gap: 28,
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#fafafa',
          borderRadius: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Space size={10}>
          <TeamOutlined />
          <Text type="secondary" style={{ fontSize: 14 }}>
            Queues:
          </Text>
          <Text strong style={{ fontSize: 14 }}>
            {displayedQueues.length}
          </Text>
        </Space>
        <Space size={10}>
          <UserOutlined />
          <Text type="secondary" style={{ fontSize: 14 }}>
            Total Tasks:
          </Text>
          <Text strong style={{ fontSize: 14 }}>
            {totalTasks}
          </Text>
        </Space>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 42,
              padding: '6px 12px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Monitored queues only
            </Text>
            <Switch
              checked={!showAllQueues}
              onChange={(checked) => setShowAllQueues(!checked)}
            />
          </div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              minHeight: 42,
              padding: '6px 12px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 999,
            }}
          >
            <Text strong style={{ fontSize: 14 }}>
              Waiting only
            </Text>
            <Switch checked={showWaitingOnly} onChange={setShowWaitingOnly} />
          </div>
        </div>
      </div>

      {/* Queue Cards */}
      {displayedQueues.length === 0 ? (
        <Empty
          description={
            showWaitingOnly
              ? 'No queues with waiting patients'
              : 'No active queues'
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: queueGridTemplateColumns,
            gap: 14,
            alignItems: 'start',
          }}
        >
          {displayedQueues.map(({ queue, visibleCallEntries }) => {
            // Every displayed queue has at least one visible patient, so the
            // badge reflects the visible (filtered) count, keeping the sum of
            // badges consistent with the "Total Tasks" summary above.
            const visibleTaskCount = visibleCallEntries.length;

            return (
              <Card
                key={queue.sid}
                size="small"
                style={{
                  backgroundColor: '#f6ffed',
                  borderColor: '#b7eb8f',
                }}
                styles={{ body: { padding: 18 } }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 14,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 8,
                    }}
                  >
                    <Text strong style={{ fontSize: 16, lineHeight: 1.35 }}>
                      {queue.name}
                    </Text>
                    <Badge
                      count={visibleTaskCount}
                      style={{
                        backgroundColor: '#52c41a',
                      }}
                    />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <Space size={6}>
                      <UserOutlined style={{ color: '#52c41a' }} />
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        {queue.availableWorkers} available
                      </Text>
                    </Space>
                  </div>

                  {queue.tasksByStatus &&
                    Object.keys(queue.tasksByStatus).length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          minHeight: 40,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Text strong style={{ fontSize: 14 }}>
                          Status:
                        </Text>
                        {Object.entries(queue.tasksByStatus).map(
                          ([status, count]) => (
                            <Tag
                              key={status}
                              color={getStatusColor(status)}
                              style={{
                                fontSize: 13,
                                padding: '4px 10px',
                                margin: 0,
                                borderRadius: 999,
                              }}
                            >
                              {getStatusLabel(status)}: {count}
                            </Tag>
                          )
                        )}
                      </div>
                    )}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <Text strong style={{ fontSize: 14 }}>
                        Patients
                      </Text>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        maxHeight: patientListMaxHeight,
                        overflowY: 'auto',
                        paddingRight: 4,
                      }}
                    >
                      {visibleCallEntries.map(
                        ({ key, patientNames, triageCat, waitTime, call }) => (
                          <div
                            key={key}
                            style={{
                              padding: '10px 12px',
                              backgroundColor: '#fafafa',
                              borderRadius: 6,
                              borderLeft: `4px solid ${getTriageBorderColor(triageCat)}`,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 6,
                              }}
                            >
                              <Space size={6}>
                                {triageCat && (
                                  <Tag
                                    color={getTriageCatColor(triageCat)}
                                    style={{
                                      fontSize: 12,
                                      margin: 0,
                                      padding: '4px 10px',
                                      borderRadius: 999,
                                    }}
                                  >
                                    {triageCat}
                                  </Tag>
                                )}
                                {waitTime !== null &&
                                  call.currentStatus !== 'wrapping' && (
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: 13 }}
                                    >
                                      {formatWaitMinutes(waitTime)}
                                    </Text>
                                  )}
                              </Space>
                              <Tag
                                color={getStatusColor(call.currentStatus)}
                                style={{
                                  fontSize: 12,
                                  margin: 0,
                                  padding: '4px 10px',
                                  borderRadius: 999,
                                }}
                              >
                                {getStatusLabel(call.currentStatus) ||
                                  'pending'}
                              </Tag>
                            </div>

                            <div
                              style={{
                                marginBottom: call.agentName ? 4 : 0,
                              }}
                            >
                              {patientNames.map((name, pIdx) => (
                                <Text
                                  key={pIdx}
                                  style={{
                                    fontSize: 15,
                                    display: 'block',
                                    lineHeight: 1.45,
                                  }}
                                >
                                  {name}
                                </Text>
                              ))}
                            </div>

                            {call.agentName && (
                              <Text type="secondary" style={{ fontSize: 13 }}>
                                {call.agentName}
                              </Text>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QueueInfoPanel;
